// =============================================================================
// app/src/lib/lote.ts — o PDF do dia virando um documento por página.
//
// Pedido do dono (25/09/2026): "eu lhe envio o pdf com todos os documentos que
// capturei no dia e você os separa em documentos únicos (criação de pdf a
// partir da página do pdf)".
//
// Ele fotografa os boletos do dia de uma vez no CamScanner e sai um PDF só,
// com 8 ou 9 folhas. Cada folha é um documento diferente, de uma conta
// diferente — e o sistema guarda um anexo por conta.
//
// COMO A PÁGINA É ENCONTRADA. `fotosDoPdfEmOrdem` (lerBoleto.ts) anda pela
// ESTRUTURA do arquivo — acha cada `stream` com dicionário `/DCTDecode` —, e
// não por varredura cega de bytes. O que ela devolve, porém, são todas as
// imagens: a folha e também o logotipo do rodapé e a miniatura que o iOS grava
// junto. Quem separa é a ÁREA: no PDF real do dono as folhas têm ~3,5 milhões
// de pixels e o logotipo tinha 240×90 = 21 mil. O corte em 1 milhão está no
// meio de uma distância de 150×, e a tela mostra cada página antes de gravar
// qualquer coisa — se o corte errar, quem olha vê.
//
// POR QUE UM PDF POR PÁGINA, e não o JPEG solto: é o que o dono pediu, é o
// formato dos 90 boletos que ele já tem guardados, e é o que ele manda ao pai
// e ao contador. A imagem vai INTEIRA para dentro do PDF novo, sem
// recomprimir — o JPEG que estava lá dentro é o mesmo que sai.
// =============================================================================
import { fotosDoPdfEmOrdem } from './lerBoleto';

/** Abaixo disto é logotipo, carimbo ou miniatura — não é folha digitalizada. */
export const AREA_MINIMA_DA_FOLHA = 1_000_000;

export interface PaginaDoLote {
  /** 1, 2, 3… na ordem em que estão no PDF. */
  numero: number;
  jpeg: Uint8Array;
  largura: number;
  altura: number;
  /** 1 = cinza, 3 = cor, 4 = CMYK. Decide o /ColorSpace do PDF de uma página. */
  componentes: number;
}

export interface DimensoesDoJpeg {
  largura: number;
  altura: number;
  componentes: number;
}

/**
 * Largura, altura e número de componentes de um JPEG, pelo marcador SOF.
 *
 * Anda de marcador em marcador em vez de procurar o padrão no meio dos dados:
 * a imagem comprimida contém sequências que parecem marcador, e cair numa
 * delas daria um tamanho absurdo.
 */
export function dimensoesDoJpeg(jpeg: Uint8Array): DimensoesDoJpeg | null {
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return null;
  let i = 2;
  while (i < jpeg.length - 9) {
    if (jpeg[i] !== 0xff) { i += 1; continue; }        // preenchimento entre marcadores
    const marca = jpeg[i + 1]!;
    if (marca === 0xff) { i += 1; continue; }
    // SOF0..SOF15, menos DHT (c4), JPG (c8) e DAC (cc), que não descrevem quadro.
    if (marca >= 0xc0 && marca <= 0xcf && marca !== 0xc4 && marca !== 0xc8 && marca !== 0xcc) {
      return {
        altura: (jpeg[i + 5]! << 8) | jpeg[i + 6]!,
        largura: (jpeg[i + 7]! << 8) | jpeg[i + 8]!,
        componentes: jpeg[i + 9]!,
      };
    }
    // Marcadores sem carga: SOI, EOI, RSTn, TEM.
    if (marca === 0xd8 || marca === 0xd9 || marca === 0x01 || (marca >= 0xd0 && marca <= 0xd7)) {
      i += 2;
      continue;
    }
    const tamanho = (jpeg[i + 2]! << 8) | jpeg[i + 3]!;
    if (tamanho < 2) return null;                      // arquivo corrompido
    i += 2 + tamanho;
  }
  return null;
}

/** As folhas do PDF do dia, em ordem. Logotipo e miniatura ficam de fora. */
export function paginasDoPdf(
  pdf: Uint8Array,
  areaMinima: number = AREA_MINIMA_DA_FOLHA,
): PaginaDoLote[] {
  const paginas: PaginaDoLote[] = [];
  for (const jpeg of fotosDoPdfEmOrdem(pdf)) {
    const d = dimensoesDoJpeg(jpeg);
    if (!d || d.largura * d.altura < areaMinima) continue;
    paginas.push({
      numero: paginas.length + 1,
      jpeg,
      largura: d.largura,
      altura: d.altura,
      componentes: d.componentes,
    });
  }
  return paginas;
}

// --- O PDF de uma página --------------------------------------------------

const A4_CURTO = 595.28;   // pontos (210 mm)
const A4_LONGO = 841.89;   // pontos (297 mm)

const ESPACO_DE_COR: Record<number, string> = {
  1: '/DeviceGray',
  3: '/DeviceRGB',
  4: '/DeviceCMYK',
};

const texto = (s: string) => {
  const b = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i += 1) b[i] = s.charCodeAt(i) & 0xff;
  return b;
};

const juntar = (partes: Uint8Array[]) => {
  const total = partes.reduce((s, p) => s + p.length, 0);
  const fora = new Uint8Array(total);
  let n = 0;
  for (const p of partes) { fora.set(p, n); n += p.length; }
  return fora;
};

/** A folha A4 em que a imagem cabe inteira, e onde ela fica dentro dela. */
export function paginaA4(largura: number, altura: number) {
  const deitada = largura > altura;
  const pagW = deitada ? A4_LONGO : A4_CURTO;
  const pagH = deitada ? A4_CURTO : A4_LONGO;
  const escala = Math.min(pagW / largura, pagH / altura);
  const w = largura * escala;
  const h = altura * escala;
  return { pagW, pagH, w, h, x: (pagW - w) / 2, y: (pagH - h) / 2 };
}

const pt = (v: number) => (Math.round(v * 100) / 100).toString();

/**
 * Um PDF de UMA página com esta imagem dentro, sem recomprimir nada.
 *
 * O JPEG entra como está, num XObject /DCTDecode — é o mesmo caminho que o
 * CamScanner e o iOS usam, e por isso a folha que sai é byte a byte a que
 * estava lá dentro. A página é A4 (deitada quando a foto é deitada) e a imagem
 * vai centralizada, cabendo inteira.
 */
export function pdfDeUmaPagina(pagina: PaginaDoLote): Uint8Array {
  const cor = ESPACO_DE_COR[pagina.componentes] ?? '/DeviceRGB';
  const { pagW, pagH, w, h, x, y } = paginaA4(pagina.largura, pagina.altura);
  const desenho = `q ${pt(w)} 0 0 ${pt(h)} ${pt(x)} ${pt(y)} cm /Im0 Do Q\n`;

  const corpos: string[] = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pt(pagW)} ${pt(pagH)}]`
      + ' /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>',
  ];

  const partes: Uint8Array[] = [];
  const deslocamentos: number[] = [];
  let tamanho = 0;
  const por = (b: Uint8Array) => { partes.push(b); tamanho += b.length; };
  const porTexto = (s: string) => por(texto(s));

  porTexto('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');   // o comentário binário: o leitor sabe que não é texto

  corpos.forEach((corpo, i) => {
    deslocamentos[i] = tamanho;
    porTexto(`${i + 1} 0 obj\n${corpo}\nendobj\n`);
  });

  // 4: a imagem.
  deslocamentos[3] = tamanho;
  porTexto(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${pagina.largura}`
    + ` /Height ${pagina.altura} /ColorSpace ${cor} /BitsPerComponent 8`
    + ` /Filter /DCTDecode /Length ${pagina.jpeg.length} >>\nstream\n`);
  por(pagina.jpeg);
  porTexto('\nendstream\nendobj\n');

  // 5: o desenho (onde a imagem fica na folha).
  deslocamentos[4] = tamanho;
  porTexto(`5 0 obj\n<< /Length ${desenho.length} >>\nstream\n${desenho}endstream\nendobj\n`);

  const inicioXref = tamanho;
  let xref = 'xref\n0 6\n0000000000 65535 f \n';
  for (const d of deslocamentos) xref += `${String(d).padStart(10, '0')} 00000 n \n`;
  xref += `trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${inicioXref}\n%%EOF\n`;
  porTexto(xref);

  return juntar(partes);
}

/** "documento-3.pdf" — o nome que o arquivo leva para a conta. */
export function nomeDaPagina(numero: number, total: number): string {
  return `documento-${String(numero).padStart(total >= 10 ? 2 : 1, '0')}.pdf`;
}
